import { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollArea } from './ui';
import styles from './HorizontalScroller.module.css';

export function HorizontalScroller({
  children,
  ariaPrev,
  ariaNext,
  gap = 16,
  rowStyle,
}: {
  children: React.ReactNode;
  ariaPrev: string;
  ariaNext: string;
  gap?: number;
  rowStyle?: React.CSSProperties;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const isRtl = (el: HTMLElement) => getComputedStyle(el).direction === 'rtl';

  const update = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    // Normalize to "distance scrolled from the start", which works whether the browser reports RTL
    // scrollLeft as negative (modern spec) or positive.
    const dist = isRtl(el) ? Math.abs(el.scrollLeft) : el.scrollLeft;
    setCanPrev(dist > 1);
    setCanNext(dist < max - 1);
  }, []);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
    // Re-run when the children change so a late-arriving list re-measures.
  }, [update, children]);

  // dir: -1 = toward the start ("previous"), +1 = toward the end ("next").
  //
  // Advance one card per click, MEASURED from where the first two children actually sit rather than
  // computed as width + gap. A row is free to lay its cards out some other way — the home's tasks strip
  // OVERLAPS them with a negative margin when the band is carrying a third slot (`PiledUpJobs`'s
  // `piled`) — and a computed step would then page a full card width past the card the user can see.
  // `Math.abs` because RTL runs the offsets the other way.
  const page = (dir: -1 | 1) => {
    const el = rowRef.current;
    if (!el) return;
    const first = el.firstElementChild as HTMLElement | null;
    const second = el.children[1] as HTMLElement | undefined;
    const measured = first && second ? Math.abs(second.offsetLeft - first.offsetLeft) : 0;
    const step = measured || (first ? first.offsetWidth + gap : Math.max(el.clientWidth * 0.85, 200));
    const amount = step * dir * (isRtl(el) ? -1 : 1);
    el.scrollBy({ left: amount, behavior: 'smooth' });
  };

  const FADE = 64;
  const maskImage =
    canPrev && canNext
      ? `linear-gradient(to right, transparent, var(--ds-black) ${FADE}px, var(--ds-black) calc(100% - ${FADE}px), transparent)`
      : canNext
        ? `linear-gradient(to right, var(--ds-black) calc(100% - ${FADE}px), transparent)`
        : canPrev
          ? `linear-gradient(to right, transparent, var(--ds-black) ${FADE}px)`
          : undefined;

  return (
    <div className={styles.scroller}>
      <ScrollArea
        axis="horizontal"
        bar="hidden"
        ref={rowRef}
        className={styles.row}
        style={{ gap, WebkitMaskImage: maskImage, maskImage, ...rowStyle }}
      >
        {children}
      </ScrollArea>
      {canPrev && (
        <button type="button" aria-label={ariaPrev} className={`${styles.btn} ${styles.btnPrev}`} onClick={() => page(-1)}>
          <ScrollArrowSvg idSuffix="prev" />
        </button>
      )}
      {canNext && (
        <button type="button" aria-label={ariaNext} className={`${styles.btn} ${styles.btnNext}`} onClick={() => page(1)}>
          <ScrollArrowSvg idSuffix="next" />
        </button>
      )}
    </div>
  );
}

function ScrollArrowSvg({ idSuffix }: { idSuffix: string }) {
  const fid = `arrow-shadow-${idSuffix}`;
  return (
    <svg width="56" height="72" viewBox="0 0 56 72" fill="none" aria-hidden style={{ display: 'block' }}>
      <g filter={`url(#${fid})`}>
        {/* The disc follows the theme, like the chat FAB's does (`ChatFab`): its glyph below is
            `--ds-surface-sunken-dark`, which is near-BLACK in light and near-WHITE in dark, so a disc
            pinned to white left the arrow invisible on a dark page. Light mode is unchanged —
            `--ds-surface-sunken` is #FFFFFF there. */}
        <rect x="32" y="19" width="24" height="24" rx="12" fill="var(--ds-surface-sunken)" shapeRendering="crispEdges" />
        <path
          d="M43.4699 27.6415C43.7628 27.3487 44.2375 27.3487 44.5304 27.6415L47.3586 30.4697C47.6514 30.7626 47.6514 31.2373 47.3586 31.5302L44.5304 34.3583C44.2376 34.6512 43.7628 34.6511 43.4699 34.3583C43.1771 34.0655 43.1771 33.5907 43.4699 33.2978L45.7677 30.9999L43.4699 28.7021C43.1771 28.4093 43.1772 27.9345 43.4699 27.6415Z"
          fill="var(--ds-surface-sunken-dark)"
        />
      </g>
      <defs>
        <filter id={fid} x="0" y="0" width="72" height="72" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dx="1" dy="4" />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feMorphology radius="0.75" operator="erode" in="SourceAlpha" result="effect2_dropShadow" />
          <feOffset dx="0.444584" dy="0.444584" />
          <feGaussianBlur stdDeviation="0.314368" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.67287 0 0 0 0 0.67287 0 0 0 0 0.67287 0 0 0 0.26 0" />
          <feBlend mode="normal" in2="effect1_dropShadow" result="effect2_dropShadow" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feMorphology radius="6" operator="erode" in="SourceAlpha" result="effect3_dropShadow" />
          <feOffset dx="-8" dy="5" />
          <feGaussianBlur stdDeviation="15" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0" />
          <feBlend mode="normal" in2="effect2_dropShadow" result="effect3_dropShadow" />
          <feBlend mode="normal" in="SourceGraphic" in2="effect3_dropShadow" result="shape" />
        </filter>
      </defs>
    </svg>
  );
}
